import { ChevronUpIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { cn } from '@/services/utils';

import { Icon } from '../Icon';
import LabelPanel from '../LabelPanel/LabelPanel';

export type DropdownOption = {
    label: string;
    value: string;
};

/** Figma `variant` axis. `outline` is the bordered trigger. */
export type ButtonDropdownVariant = 'outline' | 'ghost';

export interface ButtonDropdownProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    icon?: React.ElementType;
    titleOne: string;
    titleTwo?: string;
    /** Figma `variant`. Defaults to the bordered `outline` trigger. */
    variant?: ButtonDropdownVariant;
    isDisabled?: boolean;
    titleOneHasMaxWidth?: boolean;
    isOpen?: boolean;
};

const ButtonDropdown = React.forwardRef<HTMLButtonElement, ButtonDropdownProps>(({
    icon,
    titleOne,
    titleTwo,
    variant = 'outline',
    isDisabled = false,
    titleOneHasMaxWidth = false,
    isOpen = false,
    className,
    ...props
}, ref) => {
    const isGhost = variant === 'ghost';

    const baseClasses = [
        'tw-relative',
        'tw-border-primary',
        'tw-flex',
        'tw-items-center',
        'tw-justify-between',
        'tw-w-auto',
        'tw-min-w-0',
        'tw-max-w-full',
        'tw-h-9',
        'lg:tw-h-10',
        'tw-rounded',
        'tw-border',
        'tw-border-solid',
        'tw-bg-fill-primary',
        'tw-transition-colors',
        'tw-ease-out',
        'tw-duration-100',
        'tw-gap-4',
        'tw-px-3',
        'tw-py-2',
    ];

    const stateClasses = [
        isDisabled && 'tw-text-t-inactive tw-cursor-not-allowed',
        isOpen && !isDisabled && 'tw-border-primary-active',
        isOpen && !isDisabled && [
            'focus:tw-outline-none',
            'focus:tw-border-2',
            'focus:!tw-border-primary-focus',
            'focus-visible:tw-outline-none',
            'focus-visible:tw-border-2',
            'focus-visible:!tw-border-primary-focus',
        ],
        !isDisabled && 'tw-cursor-pointer',
        !isOpen && !isDisabled && 'hover:tw-border-primary-hover',
        !isDisabled && 'active:tw-border-primary-press',
        'focus-visible:tw-outline-none',
        'focus-visible:tw-border-2',
        'focus-visible:!tw-border-primary-focus',
    ];

    const ghostBaseClasses = [
        'tw-relative',
        'tw-flex',
        'tw-items-center',
        'tw-justify-center',
        'tw-w-auto',
        'tw-min-w-0',
        'tw-max-w-full',
        'tw-h-9',
        'lg:tw-h-10',
        'tw-rounded',
        'tw-border-solid',
        'tw-border-0',
        'tw-bg-transparent',
        'tw-transition-colors',
        'tw-ease-out',
        'tw-duration-100',
        'motion-reduce:tw-transition-none',
        'tw-gap-4',
        'tw-px-3',
        'tw-py-2',
    ];

    const ghostStateClasses = [
        isDisabled && 'tw-cursor-not-allowed',
        !isDisabled && 'tw-cursor-pointer',
        !isDisabled && !isOpen && 'hover:tw-bg-fill-primary-hover',
        !isDisabled && [
            'active:tw-bg-fill-primary-press',
            'focus-visible:tw-outline-none',
            'focus-visible:tw-border-solid',
            'focus-visible:tw-border-2',
            'focus-visible:!tw-border-primary-focus',
        ],
        isOpen && !isDisabled && [
            'tw-bg-fill-primary-press',
            'focus:tw-outline-none',
            'focus:tw-border-solid',
            'focus:tw-border-2',
            'focus:!tw-border-primary-focus',
            'focus-visible:tw-outline-none',
            'focus-visible:tw-border-solid',
            'focus-visible:tw-border-2',
            'focus-visible:!tw-border-primary-focus',
        ],
    ];

    return (
        <div className={cn('tw-relative', className)}>
            <button
                type="button"
                ref={ref}
                disabled={isDisabled}
                className={cn(
                    ...(isGhost ? ghostBaseClasses : baseClasses),
                    ...(isGhost ? ghostStateClasses : stateClasses),
                    className,
                )}
                {...props}
                aria-haspopup="menu"
                aria-expanded={isOpen && !isDisabled}
            >
                <LabelPanel
                    icon={icon}
                    titleOne={titleOne}
                    titleTwo={titleTwo}
                    titleOneHasMaxWidth={titleOneHasMaxWidth}
                    backgroundClassName={isGhost ? 'tw-bg-transparent' : 'tw-bg-white'}
                    textClassName={isDisabled ? 'tw-icon-inactive' : undefined}
                    noPadding={true}
                />

                <span
                    aria-hidden="true"
                    className={cn(
                        'tw-flex',
                        'tw-items-center',
                        'tw-transition-transform',
                        'tw-duration-100',
                        'tw-ease-out',
                        'motion-reduce:tw-transition-none',
                        !isOpen && 'tw-rotate-180'
                    )}
                >
                    <Icon disabled={isDisabled} icon={ChevronUpIcon} size="xs"
                        className={cn(
                            'lg-icon-dynamic-chevron',
                            isDisabled ? 'tw-icon-inactive' : 'tw-icon-primary'
                        )} />
                </span>
            </button>
        </div>
    );
});

ButtonDropdown.displayName = 'ButtonDropdown';

export { ButtonDropdown };
